import { useEffect, useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  SalesActions,
  useAccountDispatch,
  useAccountSelector,
} from '@wajeez-web/stores/account';
import { ButtonHeadless, Spin } from '@wajeez-web/components/ui';
import {
  FormDateInput,
  FormSelectInput,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import dayjs from 'dayjs';
import {
  convertLocalDateTimeToEnd,
  convertLocalDateTimeToZero,
} from '@wajeez-web/core/utils';

type Input = Server.GetUtilityDepartmentAnalysisInput;

const fromDateDefault = convertLocalDateTimeToZero(
  dayjs(new Date()).subtract(7, 'day')
);
const toDateDefault = convertLocalDateTimeToEnd(dayjs(new Date()));

export const SalesAnalysisPage = () => {
  const accountDispatch = useAccountDispatch();

  const [isDefaultFetch, setIsDefaultFetch] = useState(true);

  const saleUsersLookups = useAccountSelector(
    ({ sales }) => sales.utilityUsersLookups
  );

  const salesDepartmentAnalysis = useAccountSelector(
    ({ sales }) => sales.getUtilityDepartmentAnalysis
  );
  const numberOfPurchases = salesDepartmentAnalysis.data?.numberOfPurchases;

  const { handleSubmit, control } = useForm<Input>({
    defaultValues: {
      fromDate: fromDateDefault,
      toDate: toDateDefault,
    },
  });

  const onSubmit: SubmitHandler<Input> = async (data) => {
    accountDispatch(SalesActions.getUtilityDepartmentAnalysisAsync(data));
  };

  useEffect(() => {
    accountDispatch(SalesActions.getUtilityUsersLookupsAsync({}));
  }, [accountDispatch]);

  useEffect(() => {
    if (isDefaultFetch) {
      accountDispatch(
        SalesActions.getUtilityDepartmentAnalysisAsync({
          fromDate: fromDateDefault,
          toDate: toDateDefault,
        })
      );

      setIsDefaultFetch(false);
    }
  }, [accountDispatch, isDefaultFetch]);

  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={`Sales Analysis`}
        withoutRightPadding={false}
        breadCrumbsLinks={[{ title: 'Sales Analysis' }]}
      >
        <div>
          <div className="w-full pt-4 pb-4 rtl:pl-8 ltr:pr-8 flex flex-col justify-start items-start">
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="pt-4 order-2"
            >
              <h1 className="text-[24px] font-bold">
                <ReturnTranslatedText
                  text={`general.${'Filters'}`}
                  origintext="Filters"
                />
              </h1>
              <div className="w-full flex flex-col justify-start items-start space-y-2">
                <div className="w-full flex justify-start items-center space-s-2">
                  <div className="w-[300px]">
                    <FormDateInput
                      name="fromDate"
                      control={control}
                      fieldType={{
                        fieldType: Server.FieldType.Date,
                        fieldOptions: {},
                        label: 'From Date',
                        required: true,
                      }}
                      widthClassName="w-full"
                      allowClear={false}
                      controlled
                    />
                  </div>
                  <div className="w-[300px]">
                    <FormDateInput
                      name="toDate"
                      control={control}
                      fieldType={{
                        fieldType: Server.FieldType.Date,
                        fieldOptions: {},
                        label: 'To Date',
                        required: true,
                      }}
                      widthClassName="w-full"
                      allowClear={false}
                      controlled
                      endOfDay
                    />
                  </div>
                </div>
                <div className="w-full flex justify-start items-center space-s-2">
                  <div className="w-[300px]">
                    <FormSelectInput
                      name="salesUserId"
                      control={control}
                      fieldType={{
                        fieldType: Server.FieldType.Select,
                        fieldOptions: {
                          selectOptions: saleUsersLookups.data?.map((u) => {
                            return {
                              value: u.id || '',
                              label: u.email || '',
                            };
                          }),
                        },
                        label: 'User',
                      }}
                      errorMessages={{ required: 'This field is required' }}
                      widthClassName="w-full"
                      showErrors
                      isClearable
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 w-[100px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-[6px]"
                  borderRadiusStyle="rounded-[6px]"
                  isDisabled={salesDepartmentAnalysis.status === 'loading'}
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Generate"
                      origintext="Generate"
                    />
                    {!isDefaultFetch &&
                      salesDepartmentAnalysis.status === 'loading' && (
                        <BiLoaderAlt className="animate-spin w-5" />
                      )}
                  </div>
                </ButtonHeadless>
              </div>
            </form>
            <div className="pb-6">
              <div className="w-[300px] p-4 rounded-[6px] shadow">
                <h2 className="text-[22px] font-bold">
                  <ReturnTranslatedText
                    text={`general.${'Number of Purchases'}`}
                    origintext="Number of Purchases"
                  />
                </h2>
                <div className="text-[19px] font-medium">
                  {numberOfPurchases === undefined ? '_' : numberOfPurchases}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayoutsContainer>
    </div>
  );
};
